<template>
  <div class="slider-container">
    <div class="input-wrapper">
      <span
        :class="[
          'input-wrapper__euro',
          { 'input-wrapper__euro--error': inputHasError(errorTypes.MIN) }
        ]"
      >
        &euro;
      </span>
      <input
        :class="[
          'form-input',
          'form-input--changed',
          'input-wrapper__input',
          { 'form-input--error': inputHasError(errorTypes.MIN) }
        ]"
        name="Minimum car value"
        type="number"
        :step="step"
        :min="options.min"
        :max="valueMax"
        :value="valueMin"
        @input="handleValueInput($event.target.value, errorTypes.MIN)"
        @keyup.enter="$event.currentTarget.blur()"
        @change="handleValueChange"
      />
      <base-validation-error
        :has-error="inputHasError(errorTypes.MIN)"
        class="input-wrapper__error"
      >
        {{ errorMessage }}
      </base-validation-error>
    </div>
    <div
      :class="[
        'slider-container__slider',
        { 'slider-container__slider--iOS': isOnAppleDevice }
      ]"
    >
      <vue-slider
        ref="slider"
        :disabled="!!errorType"
        v-bind="options"
        :show="isVisible"
        :min-range="1"
        @drag="context => preventTooltipOverflow(context)"
        @drag-end="context => changeValues(context.getValue())"
      />
      <div class="labels">
        <p v-for="label in labels" :key="label" class="labels__label">
          €{{ label }}
        </p>
      </div>
    </div>
    <div class="input-wrapper">
      <span
        :class="[
          'input-wrapper__euro',
          { 'input-wrapper__euro--error': inputHasError(errorTypes.MAX) }
        ]"
      >
        &euro;
      </span>
      <input
        :class="[
          'form-input',
          'form-input--changed',
          'input-wrapper__input',
          { 'form-input--error': inputHasError(errorTypes.MAX) }
        ]"
        name="Maximum car value"
        type="number"
        :step="step"
        :min="valueMin"
        :max="options.max"
        :value="valueMax"
        @input="handleValueInput($event.target.value, errorTypes.MAX)"
        @keyup.enter="$event.currentTarget.blur()"
        @change="handleValueChange"
      />
      <base-validation-error
        :has-error="inputHasError(errorTypes.MAX)"
        class="input-wrapper__error"
      >
        {{ errorMessage }}
      </base-validation-error>
    </div>
  </div>
</template>
<script>
import VueSlider from "vue-slider-component";

import BaseValidationError from "@/components/ui/BaseValidationError/BaseValidationError.vue";
import { getTranslateX } from "@/utils";
import { options } from "@/store/options";

const errorTypes = {
  MIN: "MIN",
  MAX: "MAX",
  ORDER: "ORDER"
};

export default {
  name: "BudgetSlider",
  components: {
    VueSlider,
    BaseValidationError
  },
  props: {
    minPrice: {
      type: Number,
      default: 0
    },
    maxPrice: {
      type: Number,
      default: 0
    },
    initialValues: {
      type: Array,
      default() {
        return [0, 0];
      }
    },
    step: {
      type: Number,
      default: 1
    },
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      valueMin: null,
      valueMax: null,
      errorType: null,
      errorMessage: null
    };
  },
  computed: {
    options() {
      return {
        value: this.initialValues,
        width: "100%",
        height: 8,
        dotSize: 20,
        min: this.minPrice,
        max: this.maxPrice,
        interval: this.step,
        show: true,
        useKeyboard: false,
        formatter: "€{value}",
        enableCross: false,
        mergeFormatter: "€{value1} ~ €{value2}",
        clickable: false,
        bgStyle: {
          backgroundColor: "#fff",
          border: "1px solid #e1e1e1"
        },
        tooltipStyle: {
          backgroundColor: "#002e6b",
          borderRadius: "0px",
          border: "1px solid #002e6b",
          padding: "5px 9px"
        },
        processStyle: {
          backgroundColor: "#eef5ff"
        },
        sliderStyle: {
          boxShadow: "none",
          border: "1px solid #e1e1e1",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }
      };
    },
    labels() {
      return [
        this.options.min,
        (this.options.max + this.options.min) / 2,
        this.options.max
      ];
    },
    initialWindowWidth() {
      return window.innerWidth;
    },
    errorTypes() {
      return errorTypes;
    },
    isOnAppleDevice() {
      return options.isOnAppleDevice();
    }
  },
  watch: {
    initialValues() {
      [this.valueMin, this.valueMax] = this.initialValues;
    }
  },
  created() {
    this.valueMin = this.minPrice;
    this.valueMax = this.maxPrice;
  },
  mounted() {
    [this.valueMin, this.valueMax] = this.initialValues;
    if (!this.$refs.slider.$refs.tooltip0 || !options.isMobile()) return null;
    this.$refs.slider.$refs.tooltip0.setAttribute("style", "left: 120%;");
    this.$refs.slider.$refs.tooltip1.setAttribute("style", "left: -40%;");
  },
  methods: {
    changeValues(values) {
      [this.valueMin, this.valueMax] = values;
      this.validatorHandler();
      !this.errorType && this.$emit("update-store", values);
    },
    handleValueChange() {
      this.validatorHandler();
      this.$refs.slider.setValue([this.valueMin, this.valueMax]);
      !this.errorType &&
        this.$emit("update-store", [this.valueMin, this.valueMax]);
    },
    handleValueInput(value, valueType) {
      if (!value || /\D+/.test(value)) return null;
      valueType == this.errorTypes.MIN
        ? (this.valueMin = value)
        : (this.valueMax = value);
    },
    inputHasError(inputErrorType) {
      return (
        this.errorType == errorTypes[inputErrorType] ||
        this.errorType == errorTypes.ORDER
      );
    },
    validatorHandler() {
      if (this.valueMin < this.options.min) {
        this.errorType = errorTypes.MIN;
        this.errorMessage = this.$t("errors.min-value-less-than", {
          value: this.options.min
        });
      } else if (this.valueMax > this.options.max) {
        this.errorType = errorTypes.MAX;
        this.errorMessage = this.$t("errors.min-value-greater-than", {
          value: this.options.max
        });
      } else if (this.valueMax < parseInt(this.valueMin)) {
        this.errorType = errorTypes.ORDER;
        this.errorMessage = this.$t("errors.invalid-values");
      } else {
        this.errorType = null;
      }
    },
    preventTooltipOverflow(context) {
      const dotStart = context.$refs.dot0;
      const dotEnd = context.$refs.dot1;
      const tooltipStart = context.$refs.tooltip0;
      const tooltipEnd = context.$refs.tooltip1;
      const mergedTooltip = context.$refs.mergedTooltip;
      const sliderWidth = context.$refs.wrap.offsetWidth;

      const tooltipStartInformation = tooltipStart.getBoundingClientRect();
      const tooltipEndInformation = tooltipEnd.getBoundingClientRect();
      const mergedTooltipInfromation = mergedTooltip.getBoundingClientRect();

      const dotStartTranslate = getTranslateX(dotStart);
      const dotEndTranslate = getTranslateX(dotEnd);

      const tooltipStartTouchesLeftBorder = dotStartTranslate < 0;
      const tooltipStartTouchesRightBorder =
        dotStartTranslate > sliderWidth - tooltipStartInformation.width / 2;
      const tooltipEndTouchesRightBorder =
        dotEndTranslate > sliderWidth - tooltipEndInformation.width / 2;

      const resetTooltipStyle = tooltip => {
        tooltip.style.left = "50%";
      };

      if (tooltipStartTouchesLeftBorder) {
        tooltipStart.style.left = `calc(50% - ${dotStartTranslate}px)`;
      } else if (tooltipStartTouchesRightBorder) {
        tooltipStart.style.left = `calc(50% + ${sliderWidth -
          dotStartTranslate -
          tooltipStartInformation.width / 2 +
          8}px)`;
      } else {
        resetTooltipStyle(tooltipStart);
      }

      if (tooltipEndTouchesRightBorder) {
        tooltipEnd.style.left = `calc(50% + ${sliderWidth -
          dotEndTranslate -
          tooltipEndInformation.width / 2 +
          8}px)`;
      } else {
        resetTooltipStyle(tooltipEnd);
      }

      if (
        dotStartTranslate - sliderWidth + (dotEndTranslate - sliderWidth) >
        -mergedTooltipInfromation.width
      ) {
        mergedTooltip.style.left =
          -(mergedTooltipInfromation.width / 2) +
          dotEndTranslate -
          dotStartTranslate +
          "px";
      } else if (tooltipStartTouchesLeftBorder) {
        mergedTooltip.style.left = `calc(50% - ${dotStartTranslate * 2.5}px)`;
      } else {
        resetTooltipStyle(mergedTooltip);
      }
    }
  }
};
</script>

<style lang="scss">
.slider-container {
  width: 100%;

  &__slider {
    padding-top: 50px;

    &--iOS {
      padding: 50px 25px 0 25px;
    }
  }

  .labels {
    position: relative;
    display: flex;
    justify-content: space-between;
    height: 20px;

    &__label {
      margin: 0;
      position: absolute;
      color: $color-grey-400;

      &:nth-of-type(1) {
        left: 0;
      }

      &:nth-of-type(2) {
        left: 50%;
        transform: translate(-50%, 0);
      }

      &:nth-of-type(3) {
        right: 0;
      }
    }
  }
}

.input-wrapper {
  display: none;
}

.vue-slider-component {
  padding: 0 !important;
  margin-bottom: 16px;
}

.vue-slider-tooltip-wrap {
  top: -15px !important;
}

.vue-slider-dot-handle::before {
  @include circle(10px);

  content: " ";
  background-color: $color-blue-300;
}

@media screen and (min-width: $media-l) {
  .slider-container {
    display: flex;
    align-items: center;

    &__slider {
      @include flex-basis(100%);

      padding: 50px 30px 0 30px;
    }
  }

  .input-wrapper {
    min-width: 210px;
    position: relative;
    display: block;

    &__input {
      padding-left: 25px !important;
    }

    &__euro {
      position: absolute;
      top: 50%;
      transform: translate(0, -50%);
      left: 15px;

      &--error {
        color: $color-red-200;
      }
    }

    &__error {
      position: absolute;
      bottom: -10px;
    }
  }
}

@media screen and (min-width: $media-xxl) {
  .input-wrapper__euro {
    transform: translate(0, -55%);
  }
}
</style>
